import React from 'react';
import {useListItem, useMergeRefs} from '@floating-ui/react';

import {mergeProps} from '../../utils';
import {RowItem, RowItemBaseProps, RowItemBody} from '../RowItem';

import {useListBoxContext} from './ListBox';
import {RowItemCheckbox} from '../RowItemCheckbox';

import type {RowItemProps, RowItemBodyProps} from '../RowItem';
import type {SelectionModeProps} from '../../hooks';

export type ListBoxItemProps = RowItemProps &
  RowItemBodyProps & {
    value: string;
  };

export const ListBoxItem = React.forwardRef<HTMLElement, ListBoxItemProps>(
  (
    {
      checkmarkStyle,
      isDisabled,
      value,
      description,
      isMultiline,
      onClick,
      onFocus,
      truncationTooltipProps,
      ...props
    },
    forwardedRef
  ) => {
    const {ref, selectionMode, ...listBoxItemProps} = useListBoxItem({
      checkmarkStyle,
      isDisabled,
      onClick,
      onFocus,
      value,
    });

    const mergedRef = useMergeRefs([forwardedRef, ref]);
    const mergedProps = mergeProps(props, listBoxItemProps);

    const Component = React.useMemo(
      () => (selectionMode === 'multiple' ? RowItemCheckbox : RowItem),
      [selectionMode]
    );
    // When `selectionMode` is `"none"`, there might not need to be any role semantics applied to the ListBoxItem.
    // If there is no `onClick` or `href`, then we should do `role="presentation"`, which we override here.
    // If there is an `href`, we should do an `<a>` with an implicit `role="link"`, which Floating UI already does for us.
    // If there is an `onClick`, we should do `role="button"`, which Floating UI already does for us.
    const role =
      selectionMode === 'none' && !onClick && !mergedProps.href
        ? 'presentation'
        : mergedProps.role;

    return (
      <Component
        ref={mergedRef}
        {...mergedProps}
        data-roving-tabindex-item
        isDisabled={isDisabled}
        role={role}
      >
        <RowItemBody
          description={description}
          isMultiline={isMultiline}
          truncationTooltipProps={truncationTooltipProps}
        >
          {props.children}
        </RowItemBody>
      </Component>
    );
  }
);

export interface UseListBoxItem<TElement extends HTMLElement = HTMLElement> {
  (args: {
    checkmarkStyle?: RowItemBaseProps['checkmarkStyle'];
    isDisabled?: boolean;
    onClick?: React.EventHandler<React.MouseEvent<TElement>>;
    onFocus?: React.EventHandler<React.FocusEvent<TElement>>;
    onMouseEnter?: React.EventHandler<React.MouseEvent<TElement>>;
    value: string;
  }): {
    ref: React.Ref<TElement>;
    checkmarkStyle: RowItemBaseProps['checkmarkStyle'];
    tabIndex: number;
    isSelected: boolean;
    selectionMode: SelectionModeProps['selectionMode'];
    onClick: React.EventHandler<React.MouseEvent<TElement>>;
    onFocus: React.EventHandler<React.FocusEvent<TElement>>;
    onMouseEnter: React.EventHandler<React.MouseEvent<TElement>>;
    'data-active': boolean;
    role?: React.AriaRole;
  };
}

export const useListBoxItem = <TElement extends HTMLElement = HTMLElement>({
  checkmarkStyle: checkmarkStyleProp,
  isDisabled,
  onClick: onClickProp,
  onFocus: onFocusProp,
  onMouseEnter: onMouseEnterProp,
  value,
}: Parameters<UseListBoxItem<TElement>>[0]): ReturnType<
  UseListBoxItem<TElement>
> => {
  const {
    checkmarkStyle: checkmarkStyleFromContext,
    getIsSelected,
    toggleSelectedValue,
    selectionMode,
    activeIndex,
    listChildRole,
    shouldSetActiveOnMouseEnter,
    onNavigate,
  } = useListBoxContext();

  const {ref, index} = useListItem();
  const isActive = activeIndex === index;
  const tabIndex = isActive ? 0 : -1;
  const isSelected = getIsSelected(value);

  const onClick = React.useCallback(
    (event: React.MouseEvent<TElement>) => {
      if (!isDisabled) {
        toggleSelectedValue(value);
        onClickProp?.(event);
      }
    },
    [isDisabled, onClickProp, toggleSelectedValue, value]
  );

  const onFocus = React.useCallback(
    (event: React.FocusEvent<TElement>) => {
      onFocusProp?.(event);
      if (!isDisabled) {
        onNavigate(index);
      }
    },
    [index, isDisabled, onFocusProp, onNavigate]
  );

  const onMouseEnter = React.useCallback(
    (event: React.MouseEvent<TElement>) => {
      if (shouldSetActiveOnMouseEnter && !isDisabled) {
        onNavigate(index);
        event.currentTarget.focus();
      }
      onMouseEnterProp?.(event);
    },
    [
      onNavigate,
      index,
      isDisabled,
      onMouseEnterProp,
      shouldSetActiveOnMouseEnter,
    ]
  );

  const checkmarkStyle = checkmarkStyleProp ?? checkmarkStyleFromContext;

  return React.useMemo(
    () => ({
      ref,
      checkmarkStyle,
      tabIndex,
      isSelected,
      selectionMode,
      onClick,
      onFocus,
      onMouseEnter,
      'data-active': isActive,
      role: listChildRole,
    }),
    [
      isActive,
      checkmarkStyle,
      tabIndex,
      isSelected,
      selectionMode,
      onClick,
      onFocus,
      onMouseEnter,
      listChildRole,
      ref,
    ]
  );
};
